import * as THREE from 'three'


const loadImg = (url) => {
    return new Promise((resolve, reject) => {
        try {
            let img = new Image();
            img.crossOrigin = "Anonymous"; //解决跨域问题
            img.onload = function () {
                resolve(img);
            };
            img.onerror = reject;
            img.src = url
        } catch (e) {
            reject(e);
            console.log(e);
        }
    });
}

const loadBlobImg = async (blob) => {
    const url = URL.createObjectURL(blob);
    try {
        return await loadImg(url);
    } finally {
        URL.revokeObjectURL(url);
    }
}
// --- Three.js Helpers ---

// Generate a dense grid of points (0..1, 0..1) for the vertex shader to distort
function createGridMesh(subdivisions) {
    const vertices = [];
    const uvs = [];
    const indices = [];

    for (let y = 0; y <= subdivisions; y++) {
        for (let x = 0; x <= subdivisions; x++) {
            const u = x / subdivisions;
            const v = y / subdivisions;
            // Position will be modified by shader, start with UVs
            vertices.push(u - 0.5, v - 0.5, 0); // Center around origin
            uvs.push(u, 1 - v); // Flip V for Three.js texture coordinates
        }
    }

    for (let y = 0; y < subdivisions; y++) {
        for (let x = 0; x < subdivisions; x++) {
            const row1 = y * (subdivisions + 1);
            const row2 = (y + 1) * (subdivisions + 1);

            const a = row1 + x;
            const b = row1 + x + 1;
            const c = row2 + x;
            const d = row2 + x + 1;

            // Two triangles per square
            indices.push(a, b, c);
            indices.push(b, d, c);
        }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geometry.setIndex(indices);

    return geometry;
}

// Helper to apply perspective transform (homography) to a point
const applyPerspectiveMatrix = (p, m) => {
    const x = p.x;
    const y = p.y;

    const numeratorX = m[0][0] * x + m[0][1] * y + m[0][2];
    const numeratorY = m[1][0] * x + m[1][1] * y + m[1][2];
    const w = m[2][0] * x + m[2][1] * y + m[2][2];

    const weight = w === 0 ? 0.00001 : w;

    return {
        x: numeratorX / weight,
        y: numeratorY / weight
    };
};

const SAFE_WEBGL_MAX_SIDE = 8192;
const SAFE_WEBGL_MAX_PIXELS = 4096 * 4096;

const toPositiveNumber = (value, fallback = 1) => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : fallback;
};

const getImageSize = (image) => {
    return {
        width: toPositiveNumber(image?.naturalWidth || image?.videoWidth || image?.width),
        height: toPositiveNumber(image?.naturalHeight || image?.videoHeight || image?.height)
    };
};

const getWebGLLimits = () => {
    const fallback = {
        maxRenderSide: SAFE_WEBGL_MAX_SIDE,
        maxTextureSide: SAFE_WEBGL_MAX_SIDE,
        maxRenderPixels: SAFE_WEBGL_MAX_PIXELS
    };

    try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (!gl) return fallback;

        const maxTextureSide = Math.min(
            SAFE_WEBGL_MAX_SIDE,
            gl.getParameter(gl.MAX_TEXTURE_SIZE) || fallback.maxTextureSide
        );
        const maxRenderSide = Math.min(
            SAFE_WEBGL_MAX_SIDE,
            maxTextureSide,
            gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) || fallback.maxRenderSide
        );
        const ext = gl.getExtension('WEBGL_lose_context');
        if (ext) ext.loseContext();

        return {
            maxRenderSide,
            maxTextureSide,
            maxRenderPixels: Math.min(SAFE_WEBGL_MAX_PIXELS, maxRenderSide * maxRenderSide)
        };
    } catch (error) {
        console.warn('SmartObjectRenderer: WebGL limits detect failed, using fallback.', error);
        return fallback;
    }
};

const getSafeCanvasSize = (width, height, limits) => {
    const maxWidth = Math.min(width, limits.maxRenderSide);
    const maxHeightByPixels = Math.max(1, Math.floor(limits.maxRenderPixels / maxWidth));

    return {
        width: Math.max(1, Math.floor(maxWidth)),
        height: Math.max(1, Math.floor(Math.min(height, limits.maxRenderSide, maxHeightByPixels)))
    };
};

// Pure JavaScript Warp Renderer Class using Three.js
class SmartObjectRenderer {
    constructor({ size, bg_img }) {
        this.canvas = document.createElement('canvas');
        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.mesh = null;
        this.texture = null;
        this.material = null;
        this.customMatrix = null;
        this.bg_img = null;
        this.tempCanvas = document.createElement('canvas');
        this.outputWidth = Math.ceil(toPositiveNumber(size?.[0]));
        this.outputHeight = Math.ceil(toPositiveNumber(size?.[1]));
        this.limits = getWebGLLimits();
        const renderSize = getSafeCanvasSize(this.outputWidth, this.outputHeight, this.limits);
        this.renderWidth = renderSize.width;
        this.renderHeight = renderSize.height;
        this.useTiling = this.renderWidth < this.outputWidth || this.renderHeight < this.outputHeight;
        this.sourceWidth = 1;
        this.sourceHeight = 1;
        this.canvas.width = this.renderWidth;
        this.canvas.height = this.renderHeight;
        this.tempCanvas.width = this.outputWidth;
        this.tempCanvas.height = this.outputHeight
        this.bg_img = bg_img;
        this.init()
    }


    // Initialize Three.js
    init() {

        try {

            // Create scene
            this.scene = new THREE.Scene();

            // Create camera (orthographic for 2D rendering)
            this.camera = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, -1, 1);

            // Create renderer
            this.renderer = new THREE.WebGLRenderer({
                canvas: this.canvas,
                alpha: true,
                antialias: !this.useTiling,
                preserveDrawingBuffer: true // Keep buffer readable for 2D compositing.
            });
            this.renderer.setPixelRatio(1);

            // Update renderer size
            this.renderer.setSize(this.renderWidth, this.renderHeight, false);
            if (this.useTiling) {
                console.warn(`SmartObjectRenderer: render ${this.outputWidth}x${this.outputHeight} in tiles up to ${this.renderWidth}x${this.renderHeight} to avoid WebGL limits.`);
            }
            // Create geometry (64x64 grid)
            const geometry = createGridMesh(64);

            // Create material with custom shaders
            const material = new THREE.ShaderMaterial({
                uniforms: {
                    uTexture: { value: null },
                    uControlPoints: { value: new Array(16).fill(new THREE.Vector2()) },
                    uProjectionMatrix: { value: new THREE.Matrix4() }
                },
                vertexShader: this.getVertexShader(),
                fragmentShader: this.getFragmentShader(),
                transparent: true,
                side: THREE.DoubleSide
            });

            this.material = material;
            this.mesh = new THREE.Mesh(geometry, material);
            this.scene.add(this.mesh);

            // Create initial texture
            this.texture = new THREE.Texture();
            this.texture.wrapS = THREE.ClampToEdgeWrapping;
            this.texture.wrapT = THREE.ClampToEdgeWrapping;
            this.texture.magFilter = THREE.LinearFilter;
            this.texture.minFilter = THREE.LinearFilter;

            return true;
        } catch (error) {
            console.error('Error initializing SmartObject:', error);
            return false;
        }
    }

    // Vertex shader for Bezier surface warping
    getVertexShader() {
        return `
        varying vec2 vUv;
        uniform vec2 uControlPoints[16];
        uniform mat4 uProjectionMatrix;

        // Bernstein polynomials
        float B0(float t) { return (1.0 - t) * (1.0 - t) * (1.0 - t); }
        float B1(float t) { return 3.0 * (1.0 - t) * (1.0 - t) * t; }
        float B2(float t) { return 3.0 * (1.0 - t) * t * t; }
        float B3(float t) { return t * t * t; }

        vec2 getBezierPoint(float u, float v) {
          vec2 p = vec2(0.0);
          
          // Basis functions for u and v
          float bu[4];
          bu[0] = B0(u); bu[1] = B1(u); bu[2] = B2(u); bu[3] = B3(u);
          
          float bv[4];
          bv[0] = B0(v); bv[1] = B1(v); bv[2] = B2(v); bv[3] = B3(v);

          int index = 0;
          for (int j = 0; j < 4; j++) {
            for (int i = 0; i < 4; i++) {
              p += uControlPoints[index] * bu[i] * bv[j];
              index++;
            }
          }
          return p;
        }

        void main() {
          vUv = uv;
          
          // Map uv (0..1) to the warped position using the control points.
          // Photoshop Mesh Points are ordered Top-Row to Bottom-Row.
          vec2 warpedPos = getBezierPoint(uv.x, 1.0 - uv.y);
          
          // Apply custom projection matrix to match original WebGL behavior
          gl_Position = uProjectionMatrix * vec4(warpedPos, 0.0, 1.0);
        }
        `;
    }

    // Fragment shader for texture rendering
    getFragmentShader() {
        return `
        precision highp float;
        varying vec2 vUv;
        uniform sampler2D uTexture;

        void main() {
          vec4 color = texture2D(uTexture, vUv);
          if (color.a < 0.01) discard;
          gl_FragColor = color;
        }
        `;
    }

    // Handle Image Loading
    async setTexture({ imageUrl, meshPoints, customMatrix, width, height }) {
        try {
            // this.init();
            const sourceImage = await this.loadTextureImage(imageUrl);
            const imageSize = getImageSize(sourceImage);
            this.sourceWidth = toPositiveNumber(width, imageSize.width);
            this.sourceHeight = toPositiveNumber(height, imageSize.height);
            const img = this.resizeTextureImage(sourceImage);

            // Update texture
            this.texture.image = img;
            this.texture.needsUpdate = true;
            this.material.uniforms.uTexture.value = this.texture;

            this.tempCanvas.width = this.outputWidth;
            this.tempCanvas.height = this.outputHeight;
            const ctx = this.tempCanvas.getContext('2d');
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            await this.drawImageToTempCanvas(ctx, this.bg_img);
            this.drawScene(ctx, meshPoints, customMatrix);
            const blob = await this.canvasToBlob(this.tempCanvas);

            return blob;
        } catch (error) {
            console.log("🚀 ~ SmartObjectRenderer ~ loadImg ~ error:", error);
        } finally {
            // 释放 Three.js 资源
            this.cleanup();
        }
    }

    async loadTextureImage(imageUrl) {
        if (typeof imageUrl == "string") {
            return await loadImg(imageUrl);
        }

        if (typeof Blob !== 'undefined' && imageUrl instanceof Blob) {
            return await loadBlobImg(imageUrl);
        }

        return imageUrl;
    }

    resizeTextureImage(image) {
        const { width, height } = getImageSize(image);
        const scale = Math.min(1, this.limits.maxTextureSide / Math.max(width, height));
        if (scale === 1) return image;

        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.floor(width * scale));
        canvas.height = Math.max(1, Math.floor(height * scale));
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        if (typeof image.close === 'function') image.close();
        console.warn(`SmartObjectRenderer: texture scaled from ${width}x${height} to ${canvas.width}x${canvas.height} to fit WebGL texture limits.`);

        return canvas;
    }

    async drawImageToTempCanvas(ctx, img) {
        if (!img) return;

        if (typeof img == "string") {
            const image = await loadImg(img);
            ctx.drawImage(image, 0, 0, this.outputWidth, this.outputHeight);
            return;
        }

        if (typeof Blob !== 'undefined' && img instanceof Blob) {
            const image = await loadBlobImg(img);
            ctx.drawImage(image, 0, 0, this.outputWidth, this.outputHeight);
            return;
        }

        ctx.drawImage(img, 0, 0, this.outputWidth, this.outputHeight);
    }

    canvasToBlob(canvas) {
        return new Promise((resolve, reject) => {
            canvas.toBlob(blob => {
                if (!blob) {
                    reject(new Error('canvas.toBlob 生成失败'));
                    return;
                }
                resolve(blob);
            }, 'image/png');
        });
    }

    // Main Draw Function
    drawScene(ctx, meshPoints, customMatrix) {
        const renderState = (!meshPoints || meshPoints.length === 0)
            ? this.getSimpleTransformState(customMatrix)
            : this.getMeshTransformState(meshPoints, customMatrix);

        if (!renderState) return;

        this.material.uniforms.uControlPoints.value = renderState.controlPoints;
        this.prepareCamera();
        this.renderStateToCanvas(ctx, renderState);
    }

    getMeshTransformState(meshPoints, customMatrix) {
        // Check if meshPoints is empty or invalid
        const rawPoints = meshPoints;
        if (rawPoints.length !== 16) return null;

        // Apply Perspective Matrix & Coordinate System Transform
        // PS: Y is Down. Three.js: Y is Up. We flip Y by negating it.
        const points = rawPoints.map(p => {
            const t = applyPerspectiveMatrix(p, customMatrix);
            return { x: t.x, y: -t.y };
        });

        // 2. Calculate Non-Transparent Bounding Box
        const boundingBox = this.calculateNonTransparentBoundingBox(points);

        const contentWidth = boundingBox.maxX - boundingBox.minX;
        const contentHeight = boundingBox.maxY - boundingBox.minY;

        const controlPoints = points.map(p => new THREE.Vector2(p.x, p.y));

        return this.createRenderState({
            boundingBox,
            contentWidth,
            contentHeight,
            controlPoints
        });
    }

    createRenderState({ boundingBox, contentWidth, contentHeight, controlPoints }) {
        if (!Number.isFinite(contentWidth) || !Number.isFinite(contentHeight) || contentWidth <= 0 || contentHeight <= 0) {
            return null;
        }

        return {
            boundingBox,
            contentWidth,
            contentHeight,
            controlPoints
        };
    }

    prepareCamera() {
        // Set camera to simple orthographic for 2D rendering
        this.camera.left = -0.5;
        this.camera.right = 0.5;
        this.camera.top = 0.5;
        this.camera.bottom = -0.5;
        this.camera.updateProjectionMatrix();

        // Reset mesh position (handled by projection matrix)
        this.mesh.position.set(0, 0, 0);
    }

    renderStateToCanvas(ctx, renderState) {
        for (let top = 0; top < this.outputHeight; top += this.renderHeight) {
            const tileHeight = Math.min(this.renderHeight, this.outputHeight - top);

            for (let left = 0; left < this.outputWidth; left += this.renderWidth) {
                const tileWidth = Math.min(this.renderWidth, this.outputWidth - left);
                this.renderTile(ctx, renderState, left, top, tileWidth, tileHeight);
            }
        }
    }

    renderTile(ctx, renderState, left, top, width, height) {
        this.setRenderCanvasSize(width, height);
        this.material.uniforms.uProjectionMatrix.value = this.getTileProjectionMatrix(renderState, left, top, width, height);
        this.renderer.clear();
        this.renderer.render(this.scene, this.camera);
        ctx.drawImage(this.canvas, 0, 0, width, height, left, top, width, height);
    }

    setRenderCanvasSize(width, height) {
        if (this.canvas.width === width && this.canvas.height === height) return;

        this.canvas.width = width;
        this.canvas.height = height;
        this.renderer.setSize(width, height, false);
    }

    getTileProjectionMatrix(renderState, left, top, width, height) {
        const { boundingBox, contentWidth, contentHeight } = renderState;
        const minX = boundingBox.minX + (left / this.outputWidth) * contentWidth;
        const maxX = boundingBox.minX + ((left + width) / this.outputWidth) * contentWidth;
        const maxY = boundingBox.maxY - (top / this.outputHeight) * contentHeight;
        const minY = boundingBox.maxY - ((top + height) / this.outputHeight) * contentHeight;
        const w = maxX - minX;
        const h = maxY - minY;

        const projectionMatrix = new THREE.Matrix4();
        const elements = [
            2 / w, 0, 0, 0,
            0, 2 / h, 0, 0,
            0, 0, 1, 0,
            -(maxX + minX) / w, -(maxY + minY) / h, 0, 1
        ];
        projectionMatrix.fromArray(elements);

        return projectionMatrix;
    }

    // Calculate the bounding box of non-transparent area by sampling the Bezier surface
    calculateNonTransparentBoundingBox(points, sampleResolution = 32) {
        // Function to check if a point is within the Bezier surface bounds
        const isWithinSurface = (u, v) => {
            // Calculate Bezier surface point
            const point = this.getBezierSurfacePoint(u, v, points);

            // Check if this point contributes to the visible area
            // For a Bezier surface with 4x4 control points, the entire parameter space [0,1]x[0,1]
            // should map to a valid surface, but we need to exclude areas that map outside
            // the convex hull of the control points (which would indicate invalid mapping)

            return this.isValidBezierPoint(point, u, v, points);
        };

        // Sample the parameter space to find actual bounds
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        let hasValidPoints = false;

        for (let v = 0; v <= sampleResolution; v++) {
            for (let u = 0; u <= sampleResolution; u++) {
                const s = u / sampleResolution;
                const t = v / sampleResolution;

                if (isWithinSurface(s, t)) {
                    const point = this.getBezierSurfacePoint(s, t, points);

                    minX = Math.min(minX, point.x);
                    maxX = Math.max(maxX, point.x);
                    minY = Math.min(minY, point.y);
                    maxY = Math.max(maxY, point.y);
                    hasValidPoints = true;
                }
            }
        }

        // Fallback to simple bounding box if sampling fails
        if (!hasValidPoints) {
            points.forEach(p => {
                minX = Math.min(minX, p.x);
                maxX = Math.max(maxX, p.x);
                minY = Math.min(minY, p.y);
                maxY = Math.max(maxY, p.y);
            });
        }

        // Add small padding to avoid edge clipping
        const padding = 1;
        return {
            minX: minX - padding,
            maxX: maxX + padding,
            minY: minY - padding,
            maxY: maxY + padding
        };
    }

    // Calculate a single point on the Bezier surface
    getBezierSurfacePoint(u, v, points) {
        // Bernstein polynomials
        const B0 = (t) => (1 - t) * (1 - t) * (1 - t);
        const B1 = (t) => 3 * (1 - t) * (1 - t) * t;
        const B2 = (t) => 3 * (1 - t) * t * t;
        const B3 = (t) => t * t * t;

        const bu = [B0(u), B1(u), B2(u), B3(u)];
        const bv = [B0(v), B1(v), B2(v), B3(v)];

        let x = 0, y = 0;
        let index = 0;

        for (let j = 0; j < 4; j++) {
            for (let i = 0; i < 4; i++) {
                x += points[index].x * bu[i] * bv[j];
                y += points[index].y * bu[i] * bv[j];
                index++;
            }
        }

        return { x, y };
    }

    // Check if a Bezier surface point is valid (within reasonable bounds)
    isValidBezierPoint(point, _u, _v, controlPoints) {
        // Calculate the convex hull bounds of control points
        let minCX = Infinity, maxCX = -Infinity;
        let minCY = Infinity, maxCY = -Infinity;

        controlPoints.forEach(p => {
            minCX = Math.min(minCX, p.x);
            maxCX = Math.max(maxCX, p.x);
            minCY = Math.min(minCY, p.y);
            maxCY = Math.max(maxCY, p.y);
        });

        // Check if the point is within a reasonable range of control points
        // Add tolerance to account for Bezier surface extension beyond control points
        const tolerance = Math.max(maxCX - minCX, maxCY - minCY) * 0.5;

        return point.x >= (minCX - tolerance) &&
            point.x <= (maxCX + tolerance) &&
            point.y >= (minCY - tolerance) &&
            point.y <= (maxCY + tolerance) &&
            !isNaN(point.x) &&
            !isNaN(point.y) &&
            isFinite(point.x) &&
            isFinite(point.y);
    }

    // Build render state with simple matrix transformation only (no mesh warping)
    getSimpleTransformState(customMatrix) {
        // Get the image dimensions
        const img = this.texture.image;
        if (!img) return null;

        const imgWidth = img.width;
        const imgHeight = img.height;
        const sourceWidth = this.sourceWidth || imgWidth;
        const sourceHeight = this.sourceHeight || imgHeight;

        // Define the four corners of the image in pixel coordinates
        const corners = [
            { x: 0, y: 0 },                 // top-left
            { x: sourceWidth, y: 0 },        // top-right
            { x: sourceWidth, y: sourceHeight }, // bottom-right
            { x: 0, y: sourceHeight }        // bottom-left
        ];

        // Apply perspective matrix to each corner
        const transformedCorners = corners.map(p => {
            const t = applyPerspectiveMatrix(p, customMatrix);
            return { x: t.x, y: -t.y }; // Flip Y for Three.js coordinate system
        });

        // Calculate bounding box of transformed image
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        transformedCorners.forEach(p => {
            minX = Math.min(minX, p.x);
            maxX = Math.max(maxX, p.x);
            minY = Math.min(minY, p.y);
            maxY = Math.max(maxY, p.y);
        });

        // Use a simpler approach: apply the transformation directly to control points

        // Calculate projection matrix to properly display the transformed mesh
        const contentWidth = maxX - minX;
        const contentHeight = maxY - minY;

        // Apply the matrix transform to control points to simulate the transformation
        const transformedControlPoints = [];
        for (let row = 0; row < 4; row++) {
            for (let col = 0; col < 4; col++) {
                const u = col / 3; // 0, 0.333, 0.667, 1
                const v = row / 3; // 0, 0.333, 0.667, 1

                // Convert normalized to pixel coordinates
                const pixelX = u * sourceWidth;
                const pixelY = v * sourceHeight;

                // Apply perspective matrix
                const transformed = applyPerspectiveMatrix({ x: pixelX, y: pixelY }, customMatrix);

                // Flip Y for Three.js and store
                transformedControlPoints.push(new THREE.Vector2(transformed.x, -transformed.y));
            }
        }

        return this.createRenderState({
            boundingBox: {
                minX,
                maxX,
                minY,
                maxY
            },
            contentWidth,
            contentHeight,
            controlPoints: transformedControlPoints
        });
    }



    // Cleanup
    cleanup() {
        // Dispose geometry
        if (this.mesh && this.mesh.geometry) {
            this.mesh.geometry.dispose();
        }

        // Dispose material and shader program
        if (this.material) {
            this.material.dispose();
        }

        // Dispose texture
        if (this.texture) {
            const textureImage = this.texture.image;
            this.texture.dispose();
            this.texture.image = null;
            if (textureImage && typeof textureImage.close === 'function') {
                textureImage.close();
            }
        }

        // Dispose renderer and WebGL context
        if (this.renderer) {
            this.renderer.dispose();
            this.renderer = null;
        }

        // Clear scene references
        if (this.scene) {
            this.scene.clear();
            this.scene = null;
        }

        // Release canvas elements
        if (this.canvas) {
            // Force release WebGL context
            const gl = this.canvas.getContext('webgl') || this.canvas.getContext('experimental-webgl');
            if (gl && gl.getExtension('WEBGL_lose_context')) {
                gl.getExtension('WEBGL_lose_context').loseContext();
            }
            this.canvas.width = 1;
            this.canvas.height = 1;
            this.canvas = null;
        }

        if (this.tempCanvas) {
            this.tempCanvas.width = 1;
            this.tempCanvas.height = 1;
            this.tempCanvas = null;
        }

        this.mesh = null;
        this.camera = null;
        this.bg_img = null;
        this.customMatrix = null;

    }
}

export default SmartObjectRenderer;
